import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; // Import remark-gfm for table support
import { Send, MessageSquarePlus, Copy, ThumbsUp, ThumbsDown, Check } from 'lucide-react'; // Added Check icon
import './ChatWindow.css';

const ChatWindow = ({ report }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState({}); // New state for feedback
  const [copiedStatus, setCopiedStatus] = useState({}); // New state for copy feedback
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (messages.length > 0) {
        scrollToBottom();
    }
  }, [messages]);

  const handleCopy = (text, messageIndex) => {
    navigator.clipboard.writeText(text);
    setCopiedStatus(prev => ({ ...prev, [messageIndex]: true }));
    setTimeout(() => {
      setCopiedStatus(prev => ({ ...prev, [messageIndex]: false }));
    }, 2000); // Show checkmark for 2 seconds
  };

  const handleLike = (messageIndex) => {
    setFeedbackStatus(prev => ({
      ...prev,
      [messageIndex]: prev[messageIndex] === 'liked' ? null : 'liked'
    }));
    // In a real application, you would send this feedback to a backend
    console.log(`Feedback for message ${messageIndex}: ${feedbackStatus[messageIndex] === 'liked' ? 'unliked' : 'liked'}`);
  };

  const handleDislike = (messageIndex) => {
    setFeedbackStatus(prev => ({
      ...prev,
      [messageIndex]: prev[messageIndex] === 'disliked' ? null : 'disliked'
    }));
    // In a real application, you would send this feedback to a backend
    console.log(`Feedback for message ${messageIndex}: ${feedbackStatus[messageIndex] === 'disliked' ? 'undisliked' : 'disliked'}`);
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post('https://start-up-idea-validator-polpewjq2-salahs-projects-e5953328.vercel.app/api/chat', {
        messages: newMessages, // System prompt is now handled dynamically in the backend
        search_enabled: searchEnabled,
        idea: report.idea, // Pass the idea to the backend
        report_summary: report.summary, // Pass the report summary
        report_score: report.feasibilityAssessment.score // Pass the report score
      });
      const assistantMessage = { role: 'assistant', content: response.data.reply };
      setMessages([...newMessages, assistantMessage]);
    } catch (error) {
      const errorMessage = { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInput('');
    setLoading(false);
    setSearchEnabled(false);
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h3>Chat with Startup Validator</h3>
        <button onClick={handleNewChat} className="new-chat-btn">
          <MessageSquarePlus size={20} /> New Chat
        </button>
      </div>
      <div className="chat-messages">
        {/* Initial assistant message - no feedback buttons */}
        <div className="message assistant">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            I've analyzed your startup idea. What would you like to discuss further? You can ask about refining the idea, marketing strategies, or anything else.
          </ReactMarkdown>
        </div>
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.role}`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
            {msg.role === 'assistant' && (
              <div className="message-actions">
                <button onClick={() => handleCopy(msg.content, index)} className={copiedStatus[index] ? 'active' : ''}>
                  {copiedStatus[index] ? <Check size={16} /> : <Copy size={16} />}
                </button>
                <button 
                  onClick={() => handleLike(index)} 
                  className={feedbackStatus[index] === 'liked' ? 'active' : ''}
                >
                  <ThumbsUp size={16} />
                </button>
                <button 
                  onClick={() => handleDislike(index)}
                  className={feedbackStatus[index] === 'disliked' ? 'active' : ''}
                >
                  <ThumbsDown size={16} />
                </button>
              </div>
            )}
          </div>
        ))}
        {loading && <div className="message assistant">Thinking...</div>}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-input-area">
        <div className="input-wrapper"> {/* New wrapper for textarea and button */}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={searchEnabled ? "Ask a question to search the web..." : "Ask a follow-up question..."}
            rows="1"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button onClick={handleSend} disabled={loading} className="send-btn">
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
